import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Send, Trash2, Loader2, MessageSquare } from 'lucide-react';

const API = '/api';

export default function CommentSection({ contentId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/social/content/${contentId}/comments`, { withCredentials: true });
      setComments(data.comments || []);
    } catch { }
    finally { setLoading(false); }
  }, [contentId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/social/content/${contentId}/comments`, { text: text.trim() }, { withCredentials: true });
      setComments(prev => [...prev, data.comment]);
      setText('');
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setSending(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/social/comments/${commentId}`, { withCredentials: true });
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      toast.success('Comment deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const ROLE_COLOR = {
    Admin: 'bg-red-100 text-red-700',
    SocialMedia: 'bg-indigo-100 text-indigo-700',
  };

  return (
    <div className="space-y-4" data-testid="comment-section">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-700">Comments ({comments.length})</h3>
      </div>

      {/* Comment list */}
      <div className="space-y-3 max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">No comments yet. Start the conversation!</p>
        ) : (
          comments.map(c => (
            <div key={c.comment_id} className="flex items-start gap-3 group" data-testid={`comment-${c.comment_id}`}>
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                  {(c.user_name || 'U').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-700">{c.user_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${ROLE_COLOR[c.user_role] || 'bg-slate-100 text-slate-500'}`}>
                    {c.user_role}
                  </span>
                  <span className="text-[10px] text-slate-300">{timeAgo(c.created_at)}</span>
                </div>
                <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{c.text}</p>
              </div>
              {(c.user_id === user?.user_id || user?.role === 'Admin') && (
                <button
                  onClick={() => deleteComment(c.comment_id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-red-500 shrink-0 mt-1"
                  title="Delete comment"
                  data-testid={`delete-comment-${c.comment_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* New comment input */}
      <div className="flex items-end gap-2 pt-2 border-t border-slate-100">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment..."
          rows={2}
          className="resize-none text-sm"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          data-testid="comment-input"
        />
        <Button
          size="icon"
          onClick={submit}
          disabled={!text.trim() || sending}
          data-testid="comment-submit-btn"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
